import { useEffect, useMemo, useState } from "react";
import type {
    SavegameImportErrorCode,
    SavegameImportStage,
    SavegameImportWorkerMessage,
    SavegamePlanResult,
    SavegameSkipReason,
} from "../domain/savegame-import";
import type { Plan } from "../domain/types";
import { type LanguageMode, translate, useUiLanguage } from "../i18n";
import { UploadIcon } from "./icons";
import { Modal } from "./Modal";

interface SavegameImportModalProps {
    readonly file: File;
    readonly replacesCurrentPlan: boolean;
    readonly onClose: () => void;
    readonly onImport: (plan: Plan) => void;
}

interface ProcessingState {
    readonly stage: SavegameImportStage;
    readonly percent: number;
    readonly result: SavegamePlanResult | null;
    readonly error: SavegameImportErrorCode | null;
}

const INITIAL_PROCESSING_STATE: ProcessingState = {
    stage: "reading",
    percent: 0,
    result: null,
    error: null,
};

function formatFileSize(size: number): string {
    const megabytes = size / (1024 * 1024);
    return `${new Intl.NumberFormat("en", { maximumFractionDigits: 1 }).format(megabytes)} MB`;
}

function savegameErrorText(code: SavegameImportErrorCode, mode: LanguageMode): string {
    if (code === "empty-file") return translate(mode, "savegameErrorEmptyFile");
    if (code === "file-too-large") return translate(mode, "savegameErrorFileTooLarge");
    if (code === "read-failed") return translate(mode, "savegameErrorReadFailed");
    if (code === "unsupported-format") return translate(mode, "savegameErrorUnsupportedFormat");
    if (code === "unsupported-site") return translate(mode, "savegameErrorUnsupportedSite");
    if (code === "worker-failed") return translate(mode, "savegameWorkerFailed");
    return translate(mode, "savegameErrorMappingFailed");
}

function skipReasonText(reason: SavegameSkipReason, mode: LanguageMode): string {
    if (reason === "unsupported-sku") return translate(mode, "skipUnsupportedSku");
    if (reason === "rack-not-found") return translate(mode, "skipRackNotFound");
    if (reason === "size-mismatch") return translate(mode, "skipSizeMismatch");
    if (reason === "rack-incompatible") return translate(mode, "skipRackIncompatible");
    return translate(mode, "skipPositionConflict");
}

export function SavegameImportModal({
    file,
    replacesCurrentPlan,
    onClose,
    onImport,
}: SavegameImportModalProps): React.JSX.Element {
    const { mode, t } = useUiLanguage();
    const [state, setState] = useState<ProcessingState>(INITIAL_PROCESSING_STATE);

    useEffect(() => {
        const worker = new Worker(new URL("../workers/savegame-import.worker.ts", import.meta.url), { type: "module" });
        worker.onmessage = (event: MessageEvent<SavegameImportWorkerMessage>): void => {
            const message = event.data;
            if (message.type === "progress") {
                setState((current) => ({ ...current, stage: message.stage, percent: message.percent }));
            } else if (message.type === "complete") {
                setState({ stage: "complete", percent: 100, result: message.result, error: null });
            } else {
                setState((current) => ({ ...current, error: message.code }));
            }
        };
        worker.onerror = (): void => {
            setState((current) => ({ ...current, error: "worker-failed" }));
        };
        worker.postMessage({ file });
        return () => worker.terminate();
    }, [file]);

    const stageLabel = useMemo(() => {
        if (state.stage === "reading") return t("savegameStageReading");
        if (state.stage === "decoding") return t("savegameStageDecoding");
        if (state.stage === "mapping") return t("savegameStageMapping");
        if (state.stage === "validating") return t("savegameStageValidating");
        return t("savegameStageComplete");
    }, [state.stage, t]);

    const result = state.result;
    const footer = (
        <>
            <button className="button" type="button" onClick={onClose}>
                {result === null && state.error === null ? t("cancel") : t("closeDialog")}
            </button>
            {result === null ? null : (
                <button className="button button-primary" type="button" onClick={() => onImport(result.plan)}>
                    <UploadIcon />
                    {t("useSavegame")}
                </button>
            )}
        </>
    );

    return (
        <Modal
            title={t("importSavegame")}
            subtitle={t("importSavegameSubtitle")}
            className="savegame-import-dialog"
            onClose={onClose}
            footer={footer}
        >
            <section className="savegame-file-card">
                <span className="savegame-file-icon">
                    <UploadIcon />
                </span>
                <div>
                    <strong>{file.name}</strong>
                    <span>{formatFileSize(file.size)}</span>
                </div>
                <span className="savegame-local-pill">{t("processedLocally")}</span>
            </section>

            {state.error === null ? (
                <section className="savegame-progress" aria-live="polite">
                    <div className="savegame-progress-heading">
                        <div>
                            <span>{result === null ? t("processingSavegame") : t("savegameReady")}</span>
                            <strong>{stageLabel}</strong>
                        </div>
                        <strong>{state.percent}%</strong>
                    </div>
                    <div
                        className="savegame-progress-track"
                        role="progressbar"
                        aria-label={stageLabel}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={state.percent}
                    >
                        <i style={{ width: `${state.percent}%` }} />
                    </div>
                    {result === null ? <p>{t("savegameBackgroundHint")}</p> : null}
                </section>
            ) : (
                <section className="savegame-error" role="alert">
                    <strong>{t("savegameImportFailed")}</strong>
                    <p>{savegameErrorText(state.error, mode)}</p>
                </section>
            )}

            {result === null ? null : <ImportResultPreview result={result} replacesCurrentPlan={replacesCurrentPlan} />}
        </Modal>
    );
}

export function ImportResultPreview({
    result,
    replacesCurrentPlan,
}: {
    readonly result: SavegamePlanResult;
    readonly replacesCurrentPlan: boolean;
}): React.JSX.Element {
    const { mode, t } = useUiLanguage();
    const summary = result.summary;
    return (
        <section className="savegame-result">
            <div className="savegame-summary-grid">
                <div>
                    <span>{t("detectedMap")}</span>
                    <strong>{summary.mapName}</strong>
                </div>
                <div>
                    <span>{t("matchedRacks")}</span>
                    <strong>
                        {summary.matchedRackCount} / {summary.saveRackCount}
                    </strong>
                </div>
                <div>
                    <span>{t("importedEquipment")}</span>
                    <strong>{summary.placementCount.toLocaleString("en")}</strong>
                </div>
                <div>
                    <span>{summary.source === "api" ? t("importSource") : t("saveFormat")}</span>
                    <strong>{summary.source === "api" ? t("localGameApi") : `CSIM${summary.saveVersion}`}</strong>
                </div>
            </div>
            <dl className="savegame-breakdown">
                <div>
                    <dt>{t("servers")}</dt>
                    <dd>{summary.importedByKind.host.toLocaleString("en")}</dd>
                </div>
                <div>
                    <dt>{t("switches")}</dt>
                    <dd>{summary.importedByKind.switch.toLocaleString("en")}</dd>
                </div>
                <div>
                    <dt>{t("gateways")}</dt>
                    <dd>{summary.importedByKind.gateway.toLocaleString("en")}</dd>
                </div>
                <div>
                    <dt>{t("securityDevices")}</dt>
                    <dd>{summary.importedByKind.security.toLocaleString("en")}</dd>
                </div>
                <div>
                    <dt>{t("patchPanels")}</dt>
                    <dd>{summary.importedByKind["patch-panel"].toLocaleString("en")}</dd>
                </div>
            </dl>
            {summary.fixedEquipmentCount === 0 ? null : (
                <p className="savegame-note">
                    {summary.fixedEquipmentCount} {t("fixedEquipmentDetected")}
                </p>
            )}
            {summary.skippedEquipmentCount === 0 ? null : (
                <div className="savegame-warning">
                    <strong>
                        {summary.skippedEquipmentCount.toLocaleString("en")} {t("equipmentSkipped")}
                    </strong>
                    <p>{t("equipmentSkippedHint")}</p>
                    {summary.skippedItems.length === 0 ? null : (
                        <ul>
                            {summary.skippedItems.map((item) => (
                                <li key={`${item.reason}:${item.name}`}>
                                    <span className="savegame-skipped-name">
                                        <span>{item.name}</span>
                                        <small>{skipReasonText(item.reason, mode)}</small>
                                    </span>
                                    <strong>{item.count}</strong>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
            {!replacesCurrentPlan ? null : <p className="savegame-replace-warning">{t("savegameReplacesPlan")}</p>}
        </section>
    );
}
